/**
 * The header's phone button, with a small menu: open Staff calls (the missed
 * count stays on the button and in the menu), and Do not disturb — Off, one
 * hour, until seven tomorrow morning, until I turn it off. While it is on,
 * a moon sits on the button. Who still rings through is chosen on the Staff
 * calls screen ("Ringtone & Do not disturb").
 *
 * A menu rather than another header button: the header must stay compact.
 */
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../design/components";
import { DND_CHOICE_LABEL, type DndChoice } from "../../domain/calls";
import { locale, t, tn } from "../../i18n";
import { useCalls } from "./CallsProvider";
import { CALL_ICONS, CallIcon } from "./CallWindow";
import m from "./CallsMenu.module.css";

const CHOICES: DndChoice[] = ["hour", "tomorrow", "forever"];

/** "until 14:00", "until tomorrow 07:00", "until you turn it off". */
export function dndUntilText(until: string | undefined, forever: boolean, now = Date.now()): string {
  if (forever || !until) return t("until you turn it off");
  const d = new Date(until);
  const time = d.toLocaleTimeString(locale(), { hour: "2-digit", minute: "2-digit" });
  const today = new Date(now);
  const tomorrow = new Date(now);
  tomorrow.setDate(today.getDate() + 1);
  if (d.toDateString() === today.toDateString()) return t("until {time}", { time });
  if (d.toDateString() === tomorrow.toDateString()) return t("until tomorrow {time}", { time });
  return t("until {time}", {
    time: d.toLocaleString(locale(), { dateStyle: "medium", timeStyle: "short" }),
  });
}

export function CallsMenuButton({
  buttonClass,
  badgeClass,
  icon,
}: {
  buttonClass: string;
  badgeClass: string;
  icon: ReactNode;
}) {
  const calls = useCalls();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLSpanElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!calls?.available) return null;
  const n = calls.missed;
  const dnd = calls.dndOn;
  const label = [
    n ? tn(n, "Staff calls — {n} missed call", "Staff calls — {n} missed calls") : t("Staff calls"),
    dnd ? t("Do not disturb is on") : "",
  ]
    .filter(Boolean)
    .join(" · ");
  const pick = async (c: DndChoice | null) => {
    await calls.setDnd(c);
    setOpen(false);
  };

  return (
    <span className={m.wrap} ref={wrap}>
      <Button
        variant="ghost"
        className={buttonClass}
        onClick={() => setOpen((o) => !o)}
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        title={t("Staff calls")}
        data-testid="header-calls"
        data-dnd={dnd || undefined}
      >
        {icon}
        {n > 0 && (
          <span className={badgeClass} data-testid="missed-badge">
            {n > 9 ? "9+" : n}
          </span>
        )}
        {dnd && (
          <span className={m.dndBadge} data-testid="dnd-badge" aria-hidden="true">
            <CallIcon d={CALL_ICONS.moon} size={10} />
          </span>
        )}
      </Button>
      {open && (
        <div className={m.menu} role="menu" aria-label={t("Staff calls")} data-testid="calls-menu">
          <div className={m.status}>
            <span className={m.statusDot} data-dnd={dnd} />
            <span>
              {dnd ? t("Do not disturb") : t("Available for calls")}
              <span className={m.statusSub}>
                {dnd
                  ? t("Calls do not ring, {until}. Callers see a missed call.", {
                      until: dndUntilText(calls.dnd.until, calls.dnd.forever),
                    })
                  : t("Calls ring on this device.")}
              </span>
            </span>
          </div>
          <button
            type="button"
            role="menuitem"
            className={m.item}
            onClick={() => {
              setOpen(false);
              navigate("/calls");
            }}
            data-testid="menu-open-calls"
          >
            <CallIcon size={16} /> {t("Open Staff calls")}
            {n > 0 && <span className={m.count}>{tn(n, "{n} missed", "{n} missed")}</span>}
          </button>
          <div className={m.sep} />
          <div className={m.label}>{t("Do not disturb")}</div>
          <button
            type="button"
            role="menuitemradio"
            aria-checked={!dnd}
            className={m.item}
            disabled={calls.dndBusy}
            onClick={() => void pick(null)}
            data-testid="dnd-off"
          >
            <span className={m.check}>{!dnd ? "✓" : ""}</span>
            {t("Off")}
          </button>
          {CHOICES.map((c) => (
            <button
              key={c}
              type="button"
              role="menuitemradio"
              aria-checked={dnd && c === "forever" && calls.dnd.forever}
              className={m.item}
              disabled={calls.dndBusy}
              onClick={() => void pick(c)}
              data-testid={`dnd-${c}`}
            >
              <span className={m.check}>{dnd && c === "forever" && calls.dnd.forever ? "✓" : ""}</span>
              {t(DND_CHOICE_LABEL[c])}
            </button>
          ))}
          {calls.dndError && <div className={m.error}>{calls.dndError}</div>}
          <button
            type="button"
            role="menuitem"
            className={m.item}
            onClick={() => {
              setOpen(false);
              navigate("/calls#ringtone");
            }}
          >
            <span className={m.check} />
            {t("Ringtone and who rings through…")}
          </button>
        </div>
      )}
    </span>
  );
}
